import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient from '../httpClient';
import { getImpersonationEndpoints } from '../apiUtils';

export interface ImpersonatableCustomer {
    id: string;
    name: string;
    email: string;
    status: string;
    created_at: string;
}

export interface ImpersonatableUser {
    id: string;
    name: string;
    email: string;
    status: string;
    is_tenant_manager: boolean;
    tenant_id: string;
    created_at: string;
}

export interface ImpersonationStatus {
    is_impersonating: boolean;
    impersonator: {
        id: string;
        name: string;
        email: string;
    } | null;
    impersonated_customer: {
        id: string;
        name: string;
        email: string;
    } | null;
    impersonated_user: {
        id: string;
        name: string;
        email: string;
    } | null;
    started_at: string | null;
    impersonation_type: 'customer' | 'user' | null;
}

export interface ImpersonationResponse {
    success: boolean;
    data: {
        customer?: any;
        user?: any;
        impersonation_info: {
            impersonator_name: string;
            impersonator_email: string;
            started_at: string;
            is_impersonating?: boolean;
            was_impersonating?: boolean;
            impersonated_customer_name?: string;
            impersonated_customer_email?: string;
            stopped_at?: string;
        };
    };
}

export interface ImpersonationCustomersResponse {
    success: boolean;
    data: ImpersonatableCustomer[];
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
}

export interface ImpersonationUsersResponse {
    success: boolean;
    data: ImpersonatableUser[];
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
}

// Start impersonation (CRM only)
export const useStartImpersonation = () => {
    const queryClient = useQueryClient();
    const endpoints = getImpersonationEndpoints();

    return useMutation<ImpersonationResponse, Error, { customer_id: string }>({
        mutationFn: async ({ customer_id }) => {
            // Only CRM can start impersonation
            if (!endpoints.start) {
                throw new Error('Starting impersonation is only available in CRM context');
            }

            const response = await httpClient.post(endpoints.start, {
                customer_id,
            });
            return response.data;
        },
        onSuccess: () => {
            // Invalidate relevant queries
            queryClient.invalidateQueries({ queryKey: ['impersonation-status'] });
            queryClient.invalidateQueries({ queryKey: ['user-profile'] });
            // Clear sessionStorage fallback
            try {
                sessionStorage.removeItem('impersonation_status');
            } catch (e) {
                // Ignore storage errors
            }
        },
    });
};

// Stop impersonation
export const useStopImpersonation = () => {
    const queryClient = useQueryClient();
    const endpoints = getImpersonationEndpoints();

    return useMutation<ImpersonationResponse, Error>({
        mutationFn: async () => {
            const response = await httpClient.post(endpoints.stop);
            return response.data;
        },
        onSuccess: () => {
            // Invalidate relevant queries
            queryClient.invalidateQueries({ queryKey: ['impersonation-status'] });
            queryClient.invalidateQueries({ queryKey: ['user-profile'] });
            // Clear sessionStorage fallback
            try {
                sessionStorage.removeItem('impersonation_status');
            } catch (e) {
                // Ignore storage errors
            }
        },
    });
};

// Get impersonation status
export const useImpersonationStatus = () => {
    const endpoints = getImpersonationEndpoints();

    return useQuery<ImpersonationStatus>({
        queryKey: ['impersonation-status'],
        queryFn: async () => {
            const response = await httpClient.get(endpoints.status);
            // Return the inner data object from the API response
            const status = response.data.data || response.data;

            // Store in sessionStorage as fallback
            if (status?.is_impersonating) {
                try {
                    sessionStorage.setItem('impersonation_status', JSON.stringify(status));
                } catch (e) {
                    // Ignore storage errors
                }
            } else {
                // Clear storage if not impersonating
                try {
                    sessionStorage.removeItem('impersonation_status');
                } catch (e) {
                    // Ignore storage errors
                }
            }

            return status;
        },
        refetchInterval: 30000, // Refetch every 30 seconds
        // Also check sessionStorage as initial data
        initialData: () => {
            try {
                const stored = sessionStorage.getItem('impersonation_status');
                if (stored) {
                    const parsed = JSON.parse(stored);
                    if (parsed?.is_impersonating) {
                        return parsed;
                    }
                }
            } catch (e) {
                // Ignore parse errors
            }
            return undefined;
        },
    });
};

// Get list of customers that can be impersonated (CRM only)
export const useImpersonatableCustomers = (search?: string, perPage: number = 15) => {
    const endpoints = getImpersonationEndpoints();

    return useQuery<ImpersonationCustomersResponse>({
        queryKey: ['impersonation-customers', search, perPage],
        queryFn: async () => {
            // Only CRM can list customers for impersonation
            if (!endpoints.listCustomers) {
                throw new Error(
                    'Listing customers for impersonation is only available in CRM context'
                );
            }

            const params = new URLSearchParams();
            if (search) params.append('search', search);
            params.append('per_page', perPage.toString());

            const response = await httpClient.get(
                `${endpoints.listCustomers}?${params.toString()}`
            );
            return response.data;
        },
    });
};

// Clear impersonation session (CRM only)
export const useClearImpersonationSession = () => {
    const queryClient = useQueryClient();
    const endpoints = getImpersonationEndpoints();

    return useMutation({
        mutationFn: async () => {
            // Only CRM can clear impersonation session
            if (!endpoints.clearSession) {
                throw new Error('Clearing impersonation session is only available in CRM context');
            }

            const response = await httpClient.post(endpoints.clearSession);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['impersonation-status'] });
        },
    });
};

// Start user impersonation
export const useStartUserImpersonation = () => {
    const queryClient = useQueryClient();

    return useMutation<ImpersonationResponse, Error, { user_id: string }>({
        mutationFn: async ({ user_id }) => {
            const response = await httpClient.post('/tenant-manage/impersonation/start-user', {
                user_id,
            });
            return response.data;
        },
        onSuccess: () => {
            // Invalidate relevant queries
            queryClient.invalidateQueries({ queryKey: ['impersonation-status'] });
            queryClient.invalidateQueries({ queryKey: ['user-profile'] });
            // Clear sessionStorage fallback
            try {
                sessionStorage.removeItem('impersonation_status');
            } catch (e) {
                // Ignore storage errors
            }
        },
    });
};

// Get list of CRM users that can be impersonated
export const useImpersonatableUsers = (search?: string, perPage: number = 15) => {
    return useQuery<ImpersonationUsersResponse>({
        queryKey: ['impersonation-users', search, perPage],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            params.append('per_page', perPage.toString());

            const response = await httpClient.get(
                `/tenant-manage/impersonation/users?${params.toString()}`
            );
            return response.data;
        },
    });
};

// Clear impersonation cache (utility function)
export const clearImpersonationCache = (queryClient: any) => {
    queryClient.invalidateQueries({ queryKey: ['impersonation-status'] });
    queryClient.invalidateQueries({ queryKey: ['impersonation-customers'] });
    queryClient.invalidateQueries({ queryKey: ['impersonation-users'] });
};
